<div class="irembogov-table-with-input">
  <p *ngIf="field.props?.placeholder" class="text-muted small mb-2">
    {{ field.props?.placeholder ?? '' | translate }}
  </p>

  <div
    class="d-flex gap-2 mb-2"
    *ngIf="
      !isReadOnly && (showSearch || (enableRowSelection && selectedRows.length))
    ">
    <div class="search-box flex-fill" *ngIf="showSearch">
      <i class="fa-solid fa-magnifying-glass search-box-icon"></i>
      <input
        type="text"
        class="form-control"
        [placeholder]="'Search' | translate"
        [attr.aria-label]="'Search' | translate"
        [(ngModel)]="searchTerm" />
    </div>
    <button
      *ngIf="enableRowSelection && selectedRows.length"
      type="button"
      class="btn btn-bulk-delete flex-shrink-0"
      (click)="bulkDelete()">
      <i class="fa-solid fa-trash-can"></i> {{ 'Delete' | translate }} ({{
        selectedRows.length
      }})
    </button>
  </div>

  <div class="table-with-input-block">
    <irembogov-data-table
      customTableClass="table-with-input"
      [columns]="headerColumns"
      [data]="filteredRows"
      [searchable]="false"
      [selectable]="!isReadOnly && enableRowSelection"
      [pageSize]="showPagination ? itemsPerPage : rows.length || 1"
      [collectionSize]="filteredRows.length"
      [minimalPagination]="!showPagination"
      [emptyMessage]="field.props?.['emptyMessage'] ?? 'No data available'"
      (selectionChanged)="onSelectionChanged($event)">
      <ng-template
        let-row
        let-index="index"
        let-selected="selected"
        let-toggleSelect="toggleSelect">
        <tr [class.selected-row]="selected">
          <td class="select-cell" *ngIf="!isReadOnly && enableRowSelection">
            <input
              type="checkbox"
              [attr.aria-label]="'Select row' | translate"
              [checked]="selected"
              (click)="toggleSelect()"
              (keydown)="toggleSelect()" />
          </td>
          <td
            *ngFor="let column of tableColumns"
            [title]="formatCellValue(row, column)">
            {{ formatCellValue(row, column) }}
          </td>
          <td
            class="text-end actions-cell"
            *ngIf="!isReadOnly && (enableEdit || enableDeletion)">
            <button
              *ngIf="enableEdit"
              type="button"
              class="btn btn-link btn-sm"
              [attr.aria-label]="'Edit item' | translate"
              (click)="openEditPanel(row)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button
              *ngIf="enableDeletion"
              type="button"
              class="btn btn-link btn-sm text-danger"
              [attr.aria-label]="'Delete item' | translate"
              (click)="deleteRow(row)">
              <i class="fa-solid fa-trash-can"></i>
            </button>
          </td>
        </tr>
      </ng-template>
    </irembogov-data-table>

    <span *ngIf="rows.length" class="item-count-badge">
      {{ rows.length }} {{ (rows.length === 1 ? 'item' : 'items') | translate }}
    </span>

    <button
      *ngIf="!isReadOnly && !panelOpen"
      type="button"
      class="btn btn-outline-primary btn-add-item w-100"
      [disabled]="isMaxItemsReached || formControl.disabled"
      (click)="openAddPanel()">
      <i class="fa-solid fa-plus"></i> {{ 'Add Item' | translate }}
    </button>

    <div class="input-block p-3" *ngIf="panelOpen">
      <p class="input-block-title mb-3">
        {{ (panelMode === 'add' ? 'Add new item' : 'Edit item') | translate }}
      </p>
      <div
        class="alert alert-danger py-2 px-3 mb-3"
        *ngIf="uniqueFieldsCombinationError as combinationError"
        role="alert">
        <!-- values is the row's own resolved data (never itself
             translatable); message is the only translatable part, kept
             separate so it can be looked up in the translation table on its
             own instead of as part of a string that always embeds data. -->
        {{ combinationError.values
        }}{{ combinationError.isCustomMessage ? ': ' : ' '
        }}{{ combinationError.message | translate }}
      </div>
      <!-- Each column renders through its own registered Formly type (one
           formly-form per column, all bound to the same rowForm/rowModel)
           so this panel accepts any field type ui-common knows, while the
           label, required marker and messages around it stay this
           component's own — the fields are built without wrappers (see
           buildRowField) so nothing renders those a second time. The
           bootstrap checkbox carries its own inline label, which is the
           one shown for that kind. -->
      <div class="mb-3" *ngFor="let fieldSet of rowFieldSets">
        <label
          class="form-label"
          [attr.for]="fieldSet.column.key"
          *ngIf="
            !fieldSet.column.props?.hideLabel &&
            columnKind(fieldSet.column) !== 'checkbox'
          ">
          {{ fieldSet.column.props?.label ?? '' | translate }}
          <span *ngIf="fieldSet.column.props?.required" class="text-danger"
            >*</span
          >
        </label>

        <formly-form
          [form]="rowForm"
          [fields]="fieldSet.fields"
          [model]="rowModel"
          [options]="fieldSet.options"></formly-form>

        <!-- One line per validation error on the column's control. The
             wording is resolved in columnErrors (column message, then the
             panel's defaults, then formly's registered message), so any
             validator a column's type or props add shows here without a
             template change. -->
        <div
          class="text-danger small mt-1"
          *ngFor="let message of columnErrors(fieldSet.column)">
          {{ message | translate }}
        </div>
        <div
          class="text-danger small mt-1"
          *ngIf="getUniquenessError(fieldSet.column.key)">
          {{ getUniquenessError(fieldSet.column.key) ?? '' | translate }}
        </div>
      </div>

      <div class="d-flex gap-3 mt-2">
        <button
          type="button"
          class="btn btn-cancel flex-fill"
          (click)="cancelPanel()">
          {{ (panelMode === 'add' ? 'Cancel' : 'Discard Changes') | translate }}
        </button>
        <button
          type="button"
          class="btn btn-primary flex-fill"
          [disabled]="
            rowForm.invalid || (panelMode === 'edit' && rowForm.pristine)
          "
          (click)="onSave()">
          <i class="fa-solid fa-circle-check me-1"></i>
          {{ (panelMode === 'add' ? 'Save' : 'Save changes') | translate }}
        </button>
      </div>
    </div>
  </div>

  <p
    class="text-danger small mt-2"
    *ngIf="!isReadOnly && !panelOpen && isMaxItemsReached">
    {{
      'You have reached the maximum number of items you can add in the table'
        | translate
    }}
    ({{ rows.length }})
  </p>
</div>

<ng-template #confirmModalTpl let-modal>
  <div class="modal-body d-flex gap-3">
    <div
      class="confirm-icon"
      [class.confirm-icon--warning]="confirmVariant === 'warning'">
      <i
        class="fa-solid"
        [class.fa-trash-can]="confirmVariant === 'danger'"
        [class.fa-triangle-exclamation]="confirmVariant === 'warning'"></i>
    </div>
    <div>
      <h5 class="mb-2">{{ confirmTitle }}</h5>
      <p
        class="mb-0 text-muted"
        *ngIf="confirmMessageValue !== null; else plainConfirmMessage">
        {{ confirmMessagePrefix }}<strong>{{ confirmMessageValue }}</strong
        >{{ confirmMessageSuffix }}
      </p>
      <ng-template #plainConfirmMessage>
        <p class="mb-0 text-muted">{{ confirmMessage }}</p>
      </ng-template>
    </div>
  </div>
  <div class="modal-footer">
    <button type="button" class="btn btn-cancel" (click)="modal.dismiss()">
      {{ confirmCancelLabel }}
    </button>
    <button
      type="button"
      class="btn btn-danger"
      (click)="onConfirmAccept(); modal.close()">
      {{ confirmAcceptLabel }}
    </button>
  </div>
</ng-template>
